import { PageContainer } from '@ant-design/pro-components';
import { useRequest, history } from '@umijs/max';
import { Card, Tag, Button, Typography, Spin, Empty, Alert, Segmented, theme } from 'antd';
import { CheckCircleFilled, InfoCircleFilled, CalendarOutlined } from '@ant-design/icons';
import React from 'react';
import { getPlans } from '@/services/payment';
import { createStyles } from 'antd-style';
import { normalizePlanList, flattenPlanTabs, normalizeCycleKey, type CurrentSubscriptionSummary } from '@/pages/payment/utils';
import { formatLocaleMessage as t, getAppLocale } from '@/utils/i18n';
import PlanBannerBg from '@/assets/icons/plan-banner-bg.png';

const { Title, Text } = Typography;

const PLAN_CORNER_ICON_MAP: Record<string, string> = {
  thunder: require('@/assets/icons/plan-icon-basic.png'),
  thunder_standard: require('@/assets/icons/plan-icon-standard.png'),
  thunder_enterprise: require('@/assets/icons/plan-icon-enterprise.png'),
};

const getPlanCornerIcon = (plan: { displayIcon?: string; planCode?: string }) => {
  if (!plan.displayIcon) return null;
  const code = String(plan.planCode || '').toLowerCase();
  if (code.includes('enterprise')) return PLAN_CORNER_ICON_MAP.thunder_enterprise;
  if (code.includes('standard') || code === 'pro') return PLAN_CORNER_ICON_MAP.thunder_standard;
  return PLAN_CORNER_ICON_MAP.thunder;
};

const useStyles = createStyles(({ token }) => ({
  page: {
    minHeight: 'calc(100vh - 56px)',
    background: token.colorBgLayout,
  },
  pageShell: {
    width: '100%',
    maxWidth: 1280,
    margin: '0 auto',
  },
  hero: {
    padding: 0,
    marginBottom: 0,
    borderRadius: 0,
    background: 'transparent',
    border: 'none',
    boxShadow: 'none',
  },
  currentSubscriptionSummary: {
    marginInline: 'auto',
    maxWidth: 860,
    width: '100%',
    padding: '10px 20px',
    borderRadius: 14,
    background: `linear-gradient(135deg, #3b5ee8 0%, #7c4dff 50%, #b44dff 100%)`,
    backgroundImage: `url(${PlanBannerBg})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    border: 'none',
    boxShadow: '0 4px 20px -8px rgba(59, 94, 232, 0.35)',
    color: '#fff',
  },
  currentSubscriptionSummaryHeader: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    color: 'rgba(255,255,255,0.8)',
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: '0.3px',
  },
  currentSubscriptionSummaryTitle: {
    display: 'inline',
    fontSize: 16,
    fontWeight: 900,
    color: '#fff',
    lineHeight: 1.3,
  },
  currentSubscriptionSummaryDesc: {
    display: 'inline',
    fontSize: 13,
    color: 'rgba(255,255,255,0.7)',
  },
  currentSubscriptionSummaryInner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    flexWrap: 'wrap',
  },
  currentSubscriptionSummaryDivider: {
    width: 1,
    height: 14,
    background: 'rgba(255,255,255,0.3)',
    flexShrink: 0,
  },
  tabToggleWrap: {
    display: 'flex',
    justifyContent: 'center',
    margin: '16px 0',
  },
  tabToggle: {
    padding: 4,
    borderRadius: 999,
    backgroundColor: token.colorFillQuaternary,
    border: `1px solid ${token.colorBorderSecondary}`,
    '.ant-segmented-item': {
      borderRadius: 999,
      fontWeight: 700,
      color: token.colorTextSecondary,
    },
    '.ant-segmented-item-selected': {
      background: 'linear-gradient(-45deg, #2166ff 0%, #8b5cf6 100%)',
      color: '#fff',
      boxShadow: '0 4px 12px -4px rgba(33, 102, 255, 0.4)',
    },
  },
  plansGrid: {
    display: 'grid',
    gap: 14,
    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    justifyContent: 'center',
    alignItems: 'stretch',
    padding: '4px 0 18px 0',
    '@media (max-width: 640px)': {
      gridTemplateColumns: '1fr',
    },
  },
  planCard: {
    position: 'relative',
    borderRadius: 18,
    transition: 'transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease',
    border: `1.5px solid ${token.colorBorderSecondary}`,
    backgroundColor: token.colorBgContainer,
    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    cursor: 'pointer',
    '.ant-card-body': {
      padding: 0,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    },
    '&:hover': {
      transform: 'translateY(-1px)',
      boxShadow: '0 8px 18px -12px rgba(33, 102, 255, 0.15)',
    },
  },
  promoCard: {
    borderStyle: 'dashed',
    borderWidth: 2,
    borderColor: 'rgba(236, 72, 153, 0.45)',
    boxShadow: '0 2px 12px -4px rgba(236, 72, 153, 0.12)',
  },
  selectedCard: {
    borderStyle: 'dashed',
    borderWidth: 2,
    borderColor: 'rgba(99, 102, 241, 0.45)',
    boxShadow: '0 2px 12px -4px rgba(99, 102, 241, 0.15)',
  },
  currentPlanBadge: {
    background: 'linear-gradient(-45deg, #2166ff 0%, #8b5cf6 100%)',
    color: '#fff',
    borderRadius: '0 0 0 14px',
    padding: '5px 14px',
    fontSize: 12,
    fontWeight: 700,
    position: 'absolute',
    top: 0,
    right: 0,
    zIndex: 2,
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    letterSpacing: '0.3px',
    boxShadow: '0 2px 8px rgba(33, 102, 255, 0.2)',
  },
  planCornerIcon: {
    position: 'absolute',
    top: 10,
    right: 10,
    width: 18,
    height: 18,
    zIndex: 1,
    opacity: 0.8,
  },
  planCardBody: {
    width: '100%',
    height: '100%',
    padding: '16px 16px 14px',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  planHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  planIntroRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  planIntroTag: {
    flexShrink: 0,
    borderRadius: 999,
    paddingInline: 8,
    marginInlineEnd: 0,
    borderColor: 'rgba(99, 102, 241, 0.2)',
    background: 'rgba(99, 102, 241, 0.08)',
    color: '#6366f1',
    fontWeight: 700,
    fontSize: 11,
  },
  priceContainer: {
    textAlign: 'left',
    padding: '4px 0',
    background: 'transparent',
    borderRadius: 0,
    margin: 0,
    border: 'none',
  },
  price: {
    fontSize: 36,
    fontWeight: 900,
    color: token.colorTextHeading,
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'flex-start',
  },
  currency: {
    fontSize: 18,
    marginRight: 2,
    fontWeight: 600,
  },
  cycle: {
    fontSize: 14,
    color: token.colorTextSecondary,
    marginLeft: 4,
    fontWeight: 400,
  },
  featureList: {
    display: 'grid',
    gap: 6,
    marginBottom: 0,
  },
  featureItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    marginBottom: 0,
    fontSize: 13,
    lineHeight: 1.45,
  },
  checkIcon: {
    color: token.colorSuccess,
    fontSize: 15,
    marginTop: 1,
  },
  planFooter: {
    marginTop: 'auto',
  },
  subscribeButton: {
    height: 44,
    borderRadius: 16,
    fontSize: 14,
    fontWeight: 800,
    marginTop: 8,
    border: 'none',
    background: '#1e293b',
    color: '#fff',
    boxShadow: '0 4px 12px -6px rgba(30, 41, 59, 0.3)',
    transition: 'all 0.3s',
    '&:not(:disabled):hover': {
      transform: 'translateY(-1px)',
      boxShadow: '0 8px 18px -8px rgba(30, 41, 59, 0.4)',
      background: '#334155',
      color: '#fff',
    },
    '&:disabled': {
      background: token.colorFillSecondary,
      color: token.colorTextQuaternary,
      boxShadow: 'none',
    },
  },
  subscribeButtonPromo: {
    background: 'linear-gradient(135deg, #f97316 0%, #ec4899 100%)',
    boxShadow: '0 8px 16px -10px rgba(249, 115, 22, 0.4)',
    '&:not(:disabled):hover': {
      transform: 'translateY(-1px)',
      boxShadow: '0 12px 22px -12px rgba(249, 115, 22, 0.5)',
      background: 'linear-gradient(135deg, #fb923c 0%, #f472b6 100%)',
      color: '#fff',
    },
  },
}));

type Plan = ReturnType<typeof flattenPlanTabs>[number];

const getBillingCycleLabel = (billingCycle?: string) => {
  const key = String(billingCycle || '').toUpperCase();
  if (key === 'PERMANENT') return '';
  if (key === 'MONTHLY') return t('pages.payment.plans.billingCycle.month', 'month');
  if (key === 'YEARLY' || key === 'ANNUAL' || key === 'ANNUALLY') return t('pages.payment.plans.billingCycle.year', 'year');
  if (key === 'TWO_WEEK' || key === 'TWO_WEEKS' || key === 'BIWEEKLY') return t('pages.payment.plans.billingCycle.twoWeek', '2 weeks');
  if (key === 'QUARTERLY') return t('pages.payment.plans.billingCycle.quarter', 'quarter');
  return billingCycle || t('pages.payment.plans.billingCycle.generic', 'cycle');
};

const getBillingCycleTabLabel = (billingCycle?: string) => {
  const key = String(billingCycle || '').toUpperCase();
  if (key === 'MONTHLY') return t('pages.payment.plans.billingTab.month', 'Monthly');
  if (key === 'YEARLY' || key === 'ANNUAL' || key === 'ANNUALLY') return t('pages.payment.plans.billingTab.year', 'Yearly');
  if (key === 'TWO_WEEK' || key === 'TWO_WEEKS' || key === 'BIWEEKLY') return t('pages.payment.plans.billingTab.twoWeek', 'Promo');
  if (key === 'QUARTERLY') return t('pages.payment.plans.billingTab.quarter', 'Quarterly');
  if (key === 'SPECIAL_OFFER') return t('pages.payment.plans.billingTab.specialOffer', 'Special');
  return billingCycle || t('pages.payment.plans.billingTab.generic', 'Plans');
};

const isFreePrice = (price: number | string) => {
  const numericPrice = Number(price || 0);
  return !Number.isFinite(numericPrice) || numericPrice <= 0;
};

const formatPriceValue = (price: number | string) => {
  const numericPrice = Number(price || 0);
  if (!Number.isFinite(numericPrice)) {
    return '0';
  }
  if (numericPrice <= 0) {
    return t('pages.payment.plans.free', 'Free');
  }
  return numericPrice.toFixed(numericPrice % 1 === 0 ? 0 : 2);
};

const formatQuota = (quota: number | string) => {
  const n = Number(quota || 0);
  return (Number.isFinite(n) ? n : 0).toFixed(0);
};

const getRolloverText = (plan: Plan) => {
  if (plan.rolloverPolicy === 'FULL') {
    return t('pages.payment.plans.rollover.full', 'Full quota rollover');
  }
  if (plan.rolloverPolicy === 'RATIO' && plan.rolloverRatio != null) {
    return t('pages.payment.plans.rollover.ratio', '{ratio}% quota rollover', {
      ratio: Math.round(Number(plan.rolloverRatio) * 100),
    });
  }
  return t('pages.payment.plans.rollover.none', 'No quota rollover');
};

const getPlanSubtitle = (plan: Plan) => {
  const custom = String(plan.displaySubtitle || '').trim();
  if (custom) return custom;

  const code = String(plan.planCode || '').toLowerCase();
  if (code === 'free') return t('pages.payment.plans.subtitle.free', 'Best for first-time exploration');
  if (code === 'special_offer') return t('pages.payment.plans.subtitle.specialOffer', 'Great for short trials and quick onboarding');
  if (code.includes('basic')) return t('pages.payment.plans.subtitle.basic', 'Best for light daily use');
  if (code.includes('standard') || code === 'pro') return t('pages.payment.plans.subtitle.standard', 'Best for stable high-frequency use');
  if (code.includes('enterprise')) return t('pages.payment.plans.subtitle.enterprise', 'Best for teams and larger quotas');
  return t('pages.payment.plans.subtitle.default', 'Choose based on your current usage scale');
};

const getDisplayBadge = (plan: Plan) => {
  const custom = String(plan.displayBadge || '').trim();
  if (custom) return custom;
  const cycle = normalizeCycleKey(plan.billingCycle);
  if (cycle === 'TWO_WEEK') return t('pages.payment.plans.badge.twoWeek', 'Limited-time trial');
  if (cycle === 'YEARLY') return t('pages.payment.plans.badge.year', 'Better value for long-term use');
  return '';
};

const getPlanFeatures = (plan: Plan) => {
  if (Array.isArray(plan.displayFeatures) && plan.displayFeatures.length > 0) {
    return plan.displayFeatures.filter(Boolean).slice(0, 4);
  }

  const features = [
    t('pages.payment.plans.feature.credits', 'Includes {credits} credits', {
      credits: formatQuota(plan.creditQuota),
    }),
    t('pages.payment.plans.feature.duration', 'Valid for {days} days', {
      days: Number(plan.durationDays || 0) || 0,
    }),
    getRolloverText(plan),
  ];
  return features.filter(Boolean).slice(0, 4);
};

const formatSubscriptionExpiry = (value?: string) => {
  if (!value) {
    return '';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const buildSubscriptionSummary = (currentSub?: CurrentSubscriptionSummary) => {
  const daysRemaining = Number(currentSub?.daysRemaining);
  const hasSubscription = currentSub?.hasSubscription === true
    || Boolean(currentSub?.planName)
    || Boolean(currentSub?.planCode)
    || Boolean(currentSub?.currentPeriodEnd)
    || Number.isFinite(daysRemaining);

  if (!hasSubscription) {
    return null;
  }

  // daysRemaining 为 null/undefined 且无到期时间 → 永久订阅
  const isPermanent = currentSub?.daysRemaining == null && !currentSub?.currentPeriodEnd;

  const safeDays = Math.max(0, Number.isFinite(daysRemaining) ? daysRemaining : 0);
  const planName = String(
    currentSub?.planName
    || currentSub?.planCode
    || t('pages.payment.plans.summary.currentPlan', 'Current Plan'),
  );
  const expiryText = formatSubscriptionExpiry(currentSub?.currentPeriodEnd);

  if (isPermanent) {
    return {
      title: planName,
      description: t('pages.payment.plans.summary.permanent', 'Permanently active'),
    };
  }

  const remainingLabel = Number.isFinite(daysRemaining)
    ? t('pages.payment.plans.summary.remaining', '{days} days remaining', { days: safeDays })
    : '';
  const remainingText = remainingLabel
    ? `${getAppLocale() === 'zh-CN' ? '（' : ' ('}${remainingLabel}${getAppLocale() === 'zh-CN' ? '）' : ')'}`
    : '';

  return {
    title: planName,
    description: expiryText
      ? t('pages.payment.plans.summary.validUntil', 'Active until {date}{remaining}', {
        date: expiryText,
        remaining: remainingText,
      })
      : remainingLabel,
  };
};

type StartCheckoutContext =
  | { orderType: 'SUBSCRIPTION'; planCode: string; planName?: string; billingCycle: string; durationDays?: number }
  | { orderType: 'RECHARGE'; rechargeAmount: number; currency?: string; paymentMethod?: string };

const Plans: React.FC<{
  embedded?: boolean;
  onStartCheckout?: (ctx: StartCheckoutContext) => void;
}> = ({ embedded, onStartCheckout }) => {
  const { styles } = useStyles();
  const { token } = theme.useToken();

  const {
    data: planList,
    loading,
    error,
  } = useRequest(getPlans, {
    formatResult: normalizePlanList,
    cacheKey: 'payment-plans',
    staleTime: 15_000,
    onError: (e) => {
      console.error('Failed to load plans:', e);
    },
  });
  const tabs = React.useMemo(() => Array.isArray(planList?.tabs) ? planList.tabs : [], [planList?.tabs]);
  const plans = React.useMemo(() => flattenPlanTabs(tabs), [tabs]);
  const currentSub = React.useMemo(
    () => planList?.currentSubscription,
    [planList?.currentSubscription],
  );
  const currentSubscriptionSummary = React.useMemo(
    () => buildSubscriptionSummary(currentSub),
    [currentSub],
  );
  const [activeTabKey, setActiveTabKey] = React.useState<string>('');

  React.useEffect(() => {
    if (tabs.length === 0) return;
    if (tabs.some((tab) => tab.tabKey === activeTabKey)) return;
    const defaultTabKey = String(planList?.defaultTabKey || '');
    const matched = tabs.find((tab) => tab.tabKey === defaultTabKey);
    setActiveTabKey(matched?.tabKey || tabs[0].tabKey);
  }, [tabs, activeTabKey, planList?.defaultTabKey]);

  const [selectedPlanCode, setSelectedPlanCode] = React.useState<string>('');
  const activeTab = React.useMemo(
    () => tabs.find((tab) => tab.tabKey === activeTabKey) || tabs[0],
    [tabs, activeTabKey],
  );
  const cyclePlans = React.useMemo(() => activeTab?.plans || [], [activeTab]);

  React.useEffect(() => {
    const exists = cyclePlans.some(
      (p) => String(p?.planCode || '').toLowerCase() === String(selectedPlanCode || '').toLowerCase(),
    );
    if (!exists && selectedPlanCode) {
      setSelectedPlanCode('');
    }
  }, [selectedPlanCode, cyclePlans]);

  const handleSubscribe = (plan: Plan) => {
    if (embedded && onStartCheckout) {
      onStartCheckout({
        orderType: 'SUBSCRIPTION',
        planCode: String(plan.planCode || ''),
        planName: String(plan.planName || ''),
        billingCycle: String(plan.billingCycle || ''),
        durationDays: Number(plan.durationDays || 0) || undefined,
      });
      return;
    }

    const params = new URLSearchParams();
    params.set('orderType', 'SUBSCRIPTION');
    params.set('planCode', String(plan.planCode || ''));
    params.set('billingCycle', String(plan.billingCycle || ''));
    history.push(`/payment/checkout?${params.toString()}`);
  };

  if (loading) {
    if (embedded) {
      return (
        <div className={styles.pageShell} style={{ textAlign: 'center', padding: 100 }}>
          <Spin size="large" />
        </div>
      );
    }

    return (
      <PageContainer
        className={styles.page}
        ghost={false}
        title={t('pages.payment.plans.title', 'Choose a subscription plan')}
        subTitle={t('pages.payment.plans.pageSubtitle', 'Compare plan benefits and choose the subscription that matches your current usage scale.')}
      >
        <div className={styles.pageShell} style={{ textAlign: 'center', padding: 100 }}>
          <Spin size="large" />
        </div>
      </PageContainer>
    );
  }

  const content = (
    <div className={styles.pageShell} style={{ padding: embedded ? 0 : '12px 0 0' }}>
      <div className={styles.hero}>
        {embedded && (
          <Title level={3} style={{ textAlign: 'center', marginBottom: 8 }}>
            {t('pages.payment.plans.title', 'Choose a subscription plan')}
          </Title>
        )}
        {currentSubscriptionSummary && (
          <div className={styles.currentSubscriptionSummary}>
            <div className={styles.currentSubscriptionSummaryInner}>
                <span className={styles.currentSubscriptionSummaryHeader}>
                  <CalendarOutlined />
                  <span>{t('pages.payment.plans.summary.active', 'Current active subscription')}</span>
                </span>
              <span className={styles.currentSubscriptionSummaryTitle}>
                  {currentSubscriptionSummary.title}
                </span>
              {currentSubscriptionSummary.description && (
                <>
                  <span className={styles.currentSubscriptionSummaryDivider} />
                  <span className={styles.currentSubscriptionSummaryDesc}>
                      {currentSubscriptionSummary.description}
                    </span>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {error && (
        <Alert
          type="error"
          showIcon
          message={t('pages.payment.plans.loadFailed', 'Failed to load subscription plans')}
          description={(error as any)?.message || t('pages.payment.plans.loadFailedDesc', 'Please refresh and try again later, or ask an administrator to check the payment plan configuration.')}
          style={{ marginBottom: 24 }}
        />
      )}

      {!error && plans.length === 0 && (
        <Card>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t('pages.payment.plans.empty', 'No subscription plans are available right now. Please contact an administrator.')}
          />
        </Card>
      )}

      {!error && plans.length > 0 && (
        <>
          {tabs.length > 1 && (
            <div className={styles.tabToggleWrap}>
              <Segmented
                size="large"
                className={styles.tabToggle}
                value={activeTab?.tabKey}
                onChange={(v) => setActiveTabKey(String(v))}
                options={tabs.map((tab) => ({
                  label: tab.tabName || getBillingCycleTabLabel(tab.tabKey),
                  value: tab.tabKey,
                }))}
              />
            </div>
          )}

          {cyclePlans.length === 0 ? (
            <Card>
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={activeTab?.tabName ? t('pages.payment.plans.emptyGroup', 'No {group} plans are available right now', { group: activeTab.tabName }) : t('pages.payment.plans.empty', 'No subscription plans are available right now. Please contact an administrator.')}
              />
            </Card>
          ) : (
            <div className={styles.plansGrid}>
              {cyclePlans.map((plan) => {
                const purchaseLimit = Number((plan as any).purchaseLimit);
                const isSelected =
                  String(selectedPlanCode || '').toLowerCase() === String(plan.planCode || '').toLowerCase();
                const isCurrentVariant = Boolean(plan.current);
                const actionDisabled = Boolean(plan.actionDisabled);
                const actionLabel = String(
                  plan.actionLabel ||
                  (isCurrentVariant
                    ? t('pages.payment.plans.currentSubscription', 'Current Plan')
                    : actionDisabled
                      ? t('pages.payment.plans.cta.subscribed', 'Subscribed to another plan')
                      : isFreePrice(plan.price)
                        ? t('pages.payment.plans.cta.tryNow', 'Try Now')
                        : t('pages.payment.plans.cta.subscribeNow', 'Subscribe Now')),
                );
                const cornerIcon = getPlanCornerIcon(plan);
                const isPromo = normalizeCycleKey(plan.billingCycle) === 'TWO_WEEK';

                return (
                  <Card
                    key={`${activeTab?.tabKey || 'tab'}-${plan.id}`}
                    className={`${styles.planCard} ${isPromo ? styles.promoCard : ''} ${isSelected && !isPromo ? styles.selectedCard : ''}`}
                    onClick={() => {
                      setSelectedPlanCode(plan.planCode);
                    }}
                    hoverable
                    role="button"
                    aria-pressed={isSelected}
                  >
                    {isCurrentVariant && (
                      <div className={styles.currentPlanBadge}>
                        <CheckCircleFilled />
                        {t('pages.payment.plans.currentSubscription', 'Current Plan')}
                      </div>
                    )}
                    {cornerIcon && !isCurrentVariant && (
                      <img src={cornerIcon} alt="" className={styles.planCornerIcon} />
                    )}

                    <div className={styles.planCardBody}>
                      <div className={styles.planHeader}>
                        <Title level={5} style={{ margin: 0, fontSize: 15, fontWeight: 900, flex: 1, minWidth: 0 }}>
                          {plan.planName}
                        </Title>
                        {getDisplayBadge(plan) && (
                          <Tag
                            className={styles.planIntroTag}
                            style={
                              isPromo
                                ? { borderColor: 'rgba(245, 108, 58, 0.25)', background: 'rgba(245, 108, 58, 0.1)', color: '#f56c3a' }
                                : undefined
                            }
                          >
                            {getDisplayBadge(plan)}
                          </Tag>
                        )}
                      </div>

                      <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.45 }}>
                        {getPlanSubtitle(plan)}
                      </Text>

                      <div className={styles.priceContainer}>
                        <div className={styles.price}>
                          {isFreePrice(plan.price) ? (
                            <span style={{ fontSize: 32, fontWeight: 900 }}>{t('pages.payment.plans.free', 'Free')}</span>
                          ) : (
                            <>
                              <span className={styles.currency}>¥</span>
                              {formatPriceValue(plan.price)}
                            </>
                          )}
                          {getBillingCycleLabel(plan.billingCycle) && (
                            <span className={styles.cycle}>/ {getBillingCycleLabel(plan.billingCycle)}</span>
                          )}
                        </div>
                      </div>

                      <div className={styles.featureList}>
                        {getPlanFeatures(plan).map((feature, index) => (
                          <div className={styles.featureItem} key={`${plan.id}-feature-${index}`}>
                            <CheckCircleFilled className={styles.checkIcon} />
                            <Text>{feature}</Text>
                          </div>
                        ))}
                        {Number.isFinite(purchaseLimit) && purchaseLimit > 0 && (
                          <div className={styles.featureItem}>
                            <InfoCircleFilled style={{ color: token.colorWarning, fontSize: 15, marginTop: 1 }} />
                            <Text type="warning">{t('pages.payment.plans.purchaseLimit', 'Purchase limit: {count} per user', { count: purchaseLimit })}</Text>
                          </div>
                        )}
                      </div>

                      <div className={styles.planFooter}>
                        <Button
                          type="default"
                          block
                          className={`${styles.subscribeButton} ${isPromo ? styles.subscribeButtonPromo : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedPlanCode(plan.planCode);
                            handleSubscribe(plan);
                          }}
                          disabled={actionDisabled || isCurrentVariant}
                        >
                          {actionLabel}
                        </Button>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}

        </>
      )}
    </div>
  );

  if (embedded) {
    return content;
  }

  return (
    <PageContainer
      className={styles.page}
      ghost={false}
      title={t('pages.payment.plans.title', 'Choose a subscription plan')}
      subTitle={t('pages.payment.plans.pageSubtitle', 'Compare plan benefits and choose the subscription that matches your current usage scale.')}
    >
      {content}
    </PageContainer>
  );
};

export default Plans;
